import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { cookieKey, storageDeprecatedKey, storageKey } from '../../app.const';
import { OrderHttpService } from '../http/order.http.service';
import { TokenStorageService } from 'libs/core/src/lib/service/token-storage.service';
import { CookieService } from 'ngx-cookie-service';
import { getRandomHash, getTopLevelDomain } from '../function/custom-function';

export interface RemoveOptions {
  /**
   * If true, treats the key as a prefix and removes all items
   * whose keys start with that prefix.
   * This option is only supported for a single string key.
   */
  byPrefix?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class StateService {
  hashID = sessionStorage.hashID ? sessionStorage.hashID : (sessionStorage.hashID = getRandomHash());

  constructor(
    protected tokenStorageService: TokenStorageService,
    protected cookieService: CookieService
  ) {}

  removeUserSessionStorage(): void {
    this.removeItem(storageKey.lastScreeningId);
    this.removeReservationStorage();
    this.removeItem(storageKey.isEvent);
    this.removeItem(storageKey.lastEventId);
  }

  clearAuthStorageAndCookies() {
    localStorage.removeItem(storageKey.token);
    this.cookieService.delete(cookieKey.external_user_token, '/', getTopLevelDomain(window.location.href));
    this.cookieService.delete(cookieKey.external_user_r_token, '/', getTopLevelDomain(window.location.href));
  }

  removeFromSessionStorage(): void {
    OrderHttpService.cacheBuster$.next();
    this.removeItem(storageKey.order);
    this.removeItem(storageKey.personalEmail);
    this.removeItem(storageKey.personalAgreements);
    this.removeItem(storageKey.isExtraFeesSelected);
    this.removeItem(storageKey.timeStampName);
    this.removeItem(storageKey.lastEpochName);
    this.removePaymentMethod();
    this.removeVouchers();
    this.removeDepricatedKeys();
  }

  removePaymentMethod() {
    this.removeItem(storageKey.selectedPaymentMethod);
  }

  removeVouchers() {
    this.removeItem(storageKey.vouchers);
  }

  removeMemberGetMemberPromotionId() {
    this.removeItem(storageKey.mgmPromotionId);
  }

  removeEmbededPaymentUrl() {
    this.removeItem(storageKey.embededPaymentUrl);
  }

  removeReservationStorage() {
    this.removeItem(storageKey.reservation, { byPrefix: true });
  }

  setReservation(reservationId, cinemaId, screeningId, timeTo: DateTime) {
    if (reservationId) {
      this.setItem(storageKey.reservationId, reservationId);
    }
    if (cinemaId) {
      this.setItem(storageKey.reservationCinemaId, cinemaId);
    }
    if (screeningId) {
      this.setItem(storageKey.reservationScreeningId, screeningId);
    }
    if (timeTo) {
      this.setItem(storageKey.reservationScreeningTimeTo, timeTo.toString());
    }
  }

  setItem(key: string, value: string) {
    sessionStorage.setItem(this.hashifyKey(key), value);
  }

  getItem(key: string) {
    return sessionStorage.getItem(this.hashifyKey(key));
  }

  getItemThenRemove(key: string) {
    const value = sessionStorage.getItem(this.hashifyKey(key));
    if (value) {
      this.removeItem(key);
    }

    return value;
  }

  removeItem(key: string, options: RemoveOptions = { byPrefix: false }) {
    if (options.byPrefix) {
      const keysToRemove: string[] = [];

      for (let i = 0; i < sessionStorage.length; i++) {
        const currentKey = sessionStorage.key(i);
        if (currentKey && currentKey.startsWith(key)) {
          keysToRemove.push(currentKey);
        }
      }

      keysToRemove.forEach((k) => sessionStorage.removeItem(k));
      return;
    }

    sessionStorage.removeItem(this.hashifyKey(key));
  }

  removeDepricatedKeys() {
    this.removeItem(storageDeprecatedKey.orderId);
  }

  private hashifyKey(key: string) {
    return [
      storageKey.timeStampName,
      storageKey.lastEpochName,
      storageKey.isEvent,
      storageKey.lastEventId,

      storageKey.chosenLocation,
      storageKey.order,
      storageKey.orderTtlExtensionsCounter,
      storageKey.lastScreeningId,
      storageKey.reservationId,
      storageKey.reservationCinemaId,
      storageKey.reservationScreeningId,

      storageKey.flowType,
      storageKey.flowData,

      storageDeprecatedKey.orderId,
      storageDeprecatedKey.chosenCinema,
      storageDeprecatedKey.chosenRegion
    ].includes(key)
      ? `${key}_${this.hashID}`
      : key;
  }
}
